import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Edit3, Plus, Power, PowerOff, Save, X } from 'lucide-react';
import { userService } from '../../services/adminService';
import type { DepartmentPayload } from '../../services/adminService';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { ErrorHandler } from '../../utils/errorHandler';
import { DepartmentSummary } from '../../types/domain';
import DismissibleError from '../DismissibleError';

interface DepartmentFormData {
  name: string;
  code: string;
  type: 'station' | 'unit';
  parent: string;
  description: string;
  is_active: boolean;
}

const emptyForm: DepartmentFormData = {
  name: '',
  code: '',
  type: 'unit',
  parent: '',
  description: '',
  is_active: true,
};

const typeLabel = (type: string) => {
  if (type === 'station') return 'Station';
  if (type === 'unit') return 'Unit';
  return type || '-';
};

const DepartmentManagement: React.FC = () => {
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<DepartmentSummary | null>(null);
  const [formData, setFormData] = useState<DepartmentFormData>(emptyForm);
  const [message, setMessage] = useState('');
  const { error, setError, clearError, getErrorMessage } = useErrorHandler();

  const activeParentOptions = useMemo(
    () => departments.filter((department) =>
      department.is_active && department.id !== editingDepartment?.id
    ),
    [departments, editingDepartment],
  );

  const loadDepartments = useCallback(async () => {
    try {
      setLoading(true);
      clearError();
      const loadedDepartments = await userService.getDepartments({ includeInactive: true });
      setDepartments(loadedDepartments);
    } catch (err) {
      setError(ErrorHandler.classifyError(err));
    } finally {
      setLoading(false);
    }
  }, [clearError, setError]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  const openCreateForm = () => {
    clearError();
    setMessage('');
    setEditingDepartment(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEditForm = (department: DepartmentSummary) => {
    clearError();
    setMessage('');
    setEditingDepartment(department);
    setFormData({
      name: department.name,
      code: department.code,
      type: department.type === 'station' ? 'station' : 'unit',
      parent: department.parent ? String(department.parent) : '',
      description: department.description || '',
      is_active: department.is_active,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingDepartment(null);
    setFormData(emptyForm);
  };

  const buildPayload = (): DepartmentPayload => ({
    name: formData.name,
    code: formData.code,
    type: formData.type,
    parent: formData.parent ? Number(formData.parent) : null,
    description: formData.description,
    is_active: formData.is_active,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      clearError();
      setMessage('');
      if (editingDepartment) {
        await userService.updateDepartment(editingDepartment.id, buildPayload());
        setMessage('Department updated.');
      } else {
        await userService.createDepartment(buildPayload());
        setMessage('Department created.');
      }
      closeForm();
      await loadDepartments();
    } catch (err) {
      setError(ErrorHandler.classifyError(err));
    }
  };

  const toggleDepartment = async (department: DepartmentSummary) => {
    try {
      clearError();
      setMessage('');
      if (department.is_active) {
        await userService.deactivateDepartment(department.id);
        setMessage('Department deactivated.');
      } else {
        await userService.activateDepartment(department.id);
        setMessage('Department activated.');
      }
      await loadDepartments();
    } catch (err) {
      setError(ErrorHandler.classifyError(err));
    }
  };

  if (loading) {
    return <div className="loading">Loading departments...</div>;
  }

  return (
    <div className="department-management">
      <div className="page-header">
        <div>
          <h1>Department Management</h1>
          <p>Create and maintain the department list used for user assignments.</p>
        </div>
        <button className="btn btn-primary" type="button" onClick={openCreateForm}>
          <Plus size={16} />Create Department
        </button>
      </div>

      {error && <DismissibleError message={getErrorMessage()} onDismiss={clearError} />}
      {message && <div className="success-message">{message}</div>}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingDepartment ? 'Edit Department' : 'Create Department'}</h2>
              <button className="close-btn" type="button" onClick={closeForm} aria-label="Close dialog"><X size={20} /></button>
            </div>

            <form onSubmit={handleSubmit} className="department-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Department Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Department Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(event) => setFormData({ ...formData, code: event.target.value.toUpperCase() })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={formData.type}
                    onChange={(event) => setFormData({ ...formData, type: event.target.value as DepartmentFormData['type'] })}
                    required
                  >
                    <option value="station">Station</option>
                    <option value="unit">Unit</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Parent Department</label>
                  <select
                    value={formData.parent}
                    onChange={(event) => setFormData({ ...formData, parent: event.target.value })}
                  >
                    <option value="">No parent department</option>
                    {activeParentOptions.map((department) => (
                      <option key={department.id} value={department.id}>{department.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(event) => setFormData({ ...formData, description: event.target.value })}
                    rows={3}
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(event) => setFormData({ ...formData, is_active: event.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn btn-primary">
                  {editingDepartment ? <Save size={16} /> : <Plus size={16} />}
                  {editingDepartment ? 'Update Department' : 'Create Department'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="departments-table records-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Type</th>
              <th>Parent Department</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {departments.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty-table-state">
                    <span className="empty-table-icon"><Building2 size={18} /></span>
                    <strong>No departments</strong>
                    <p>Create a department before assigning department-scoped users.</p>
                  </div>
                </td>
              </tr>
            ) : departments.map((department) => (
              <tr key={department.id}>
                <td>{department.name}</td>
                <td>{department.code}</td>
                <td>{typeLabel(department.type)}</td>
                <td>{department.parent_name || '-'}</td>
                <td>
                  <span className={`status ${department.is_active ? 'active' : 'inactive'}`}>
                    {department.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button className="btn btn-sm btn-secondary" type="button" onClick={() => openEditForm(department)}>
                    <Edit3 size={13} />Edit
                  </button>
                  <button
                    className={`btn btn-sm ${department.is_active ? 'btn-danger' : 'btn-secondary'}`}
                    type="button"
                    onClick={() => toggleDepartment(department)}
                  >
                    {department.is_active ? <PowerOff size={13} /> : <Power size={13} />}
                    {department.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DepartmentManagement;
